import { useState } from 'react'
import { club } from '../../api/club'
import { ApiError } from '../../api/client'

interface AddPlayerModalProps {
  clubId: number
  onClose: () => void
  seatLimit?: number | null
  playerCount?: number
}

// Backend 409 detail looks like "seat_limit_reached: cupo de licencias
// alcanzado para este club (X/Y jugadores)." — strip the machine-readable
// prefix and show only the human-readable Spanish message.
function friendlyError(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback
  const msg = e.message
  const marker = 'seat_limit_reached:'
  if (msg.startsWith(marker)) return msg.slice(marker.length).trim()
  return msg
}

export function AddPlayerModal({ clubId, onClose, seatLimit, playerCount }: AddPlayerModalProps) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ user_id: number; email: string; reused: boolean } | null>(null)

  const isFull =
    seatLimit != null && playerCount != null ? playerCount >= seatLimit : false

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) {
      setError('El email es requerido.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await club.addPlayer(clubId, {
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        category: category.trim() || undefined,
      })
      setResult(res)
    } catch (e) {
      setError(friendlyError(e, 'Error al agregar jugador.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-md rounded-t-lg bg-surface p-lg shadow-lg sm:rounded-lg">
        <h2 className="text-lg font-bold text-text-primary mb-md">Agregar jugador</h2>

        {isFull && !result && (
          <div className="banner-err mb-md">
            Cupo de licencias alcanzado ({playerCount}/{seatLimit}). Podés reasignar un
            jugador existente o pedir que se amplíe el cupo — si intentás agregar uno
            nuevo igual, el servidor lo va a rechazar.
          </div>
        )}

        {result ? (
          <div className="space-y-md">
            <div className="rounded-sm border border-success/30 bg-success/10 px-md py-sm text-sm text-text-primary">
              <p className="font-semibold">Jugador agregado con éxito</p>
              <p className="text-sm text-text-secondary mt-xs">
                {result.email}
                {result.reused && ' (ya existía)'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-primary w-full"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-md">
            {error && <div className="banner-err">{error}</div>}

            <label className="flex flex-col gap-xs text-sm">
              Email *
              <input
                type="email"
                className="input"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </label>

            <label className="flex flex-col gap-xs text-sm">
              Nombre (opcional)
              <input
                type="text"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={loading}
              />
            </label>

            <label className="flex flex-col gap-xs text-sm">
              Categoría (opcional)
              <input
                type="text"
                className="input"
                placeholder="Ej: Sub-17, Primera"
                maxLength={64}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
              />
            </label>

            <p className="text-sm text-text-secondary">
              Si es un jugador nuevo, recibirá un mail para elegir su propio PIN.
            </p>

            <div className="flex gap-sm pt-md">
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost flex-1"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
              >
                {loading ? 'Agregando…' : 'Agregar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
